import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createNote,
  deleteNote,
  listNotes,
  updateNote,
  type NoteRecord,
} from "../../../helpers/api/crm";

interface Props {
  customerId: string;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Plain-text notes panel for a customer 360. New notes post to a fresh row;
 * editing an existing note transfers authorship to the current admin (the
 * backend's `customer-note.service.ts` handles this).
 */
export function NotesSection({ customerId }: Props) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const { data, isLoading } = useQuery<{
    notes: NoteRecord[];
    total: number;
  }>({
    queryKey: ["crm", "customers", customerId, "notes"],
    queryFn: () => listNotes(customerId, { limit: 50 }),
  });

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: ["crm", "customers", customerId, "notes"],
    });
    // Dashboard row shows the most-recent note preview → refetch.
    qc.invalidateQueries({ queryKey: ["crm", "customers"] });
  };

  const create = useMutation({
    mutationFn: (body: string) => createNote(customerId, body),
    onSuccess: () => {
      setDraft("");
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: (vars: { noteId: string; body: string }) =>
      updateNote(customerId, vars.noteId, vars.body),
    onSuccess: () => {
      setEditingId(null);
      setEditDraft("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (noteId: string) => deleteNote(customerId, noteId),
    onSuccess: invalidate,
  });

  const notes = data?.notes ?? [];

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">Notes</h2>
        <span className="text-[11px] text-content-tertiary">
          {data?.total ?? 0} total
        </span>
      </div>

      <div className="p-4">
        <div className="mb-4">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add an internal note about this customer…"
            rows={2}
            className="w-full text-sm px-3 py-2 border border-border-secondary rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-y"
          />
          <div className="flex items-center justify-end gap-2 mt-1.5">
            {create.isError && (
              <span className="text-[11px] text-rose-600">Failed to save</span>
            )}
            <button
              type="button"
              disabled={!draft.trim() || create.isPending}
              onClick={() => create.mutate(draft.trim())}
              className="text-xs px-3 py-1 bg-primary text-white rounded-md disabled:opacity-50 font-medium"
            >
              {create.isPending ? "Saving…" : "Add note"}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        ) : notes.length === 0 ? (
          <p className="text-xs text-content-tertiary italic">No notes yet.</p>
        ) : (
          <ul className="space-y-2">
            {notes.map((n) => {
              const isEditing = editingId === n.id;
              return (
                <li
                  key={n.id}
                  className="group border border-border-secondary rounded-lg px-3 py-2.5 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
                      {formatDateTime(n.updatedAt ?? n.createdAt)}
                      {n.updatedAt && n.updatedAt !== n.createdAt && " · edited"}
                    </span>
                    {!isEditing && (
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(n.id);
                            setEditDraft(n.body);
                          }}
                          className="text-[11px] text-content-tertiary hover:text-primary font-medium"
                        >
                          Edit
                        </button>
                        <span className="text-content-tertiary/40">·</span>
                        <button
                          type="button"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm("Delete this note?")) {
                              remove.mutate(n.id);
                            }
                          }}
                          className="text-[11px] text-content-tertiary hover:text-rose-600 disabled:opacity-50 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <>
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={3}
                        className="w-full text-sm px-2.5 py-1.5 border border-border-secondary rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-y"
                      />
                      <div className="flex items-center justify-end gap-2 mt-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditDraft("");
                          }}
                          className="text-[11px] px-2 py-1 text-content-tertiary hover:text-content-primary"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!editDraft.trim() || update.isPending}
                          onClick={() =>
                            update.mutate({
                              noteId: n.id,
                              body: editDraft.trim(),
                            })
                          }
                          className="text-xs px-3 py-1 bg-primary text-white rounded-md disabled:opacity-50 font-medium"
                        >
                          {update.isPending ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-content-primary whitespace-pre-wrap leading-relaxed">
                      {n.body}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
